import dongchediApi from '../api/dongchediApi'

function normalizeNumber(n) {
  const v = parseFloat(n || 0)
  return Number.isFinite(v) ? v : 0
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v))
}

async function aggregateSelectionSummary(mode, selectedObjects = [], baseFilters = {}) {
  const totals = { total_count: 0, positive_count: 0, negative_count: 0, neutral_count: 0, avg_score_sum: 0, avg_score_weight: 0 }
  const dist = { positive: 0, negative: 0, neutral: 0 }
  const wordCloud = new Map()
  const topicRows = new Map() // key `${aspect}|${sentiment}` -> count

  for (const name of selectedObjects) {
    const filters = { ...(baseFilters || {}) }
    if (mode === 'series') filters.seriesName = name
    else filters.carName = name

    const [stats, sentimentDist, wordList, topicSentiment] = await Promise.all([
      dongchediApi.getSentimentStats(filters),
      dongchediApi.getSentimentDistribution(filters),
      dongchediApi.getWordCloudAspects(filters),
      dongchediApi.getTopicSentimentDistribution(filters)
    ])

    totals.total_count += normalizeNumber(stats.total_count)
    totals.positive_count += normalizeNumber(stats.positive_count)
    totals.negative_count += normalizeNumber(stats.negative_count)
    totals.neutral_count += normalizeNumber(stats.neutral_count)
    if (stats.avg_score) {
      const w = normalizeNumber(stats.total_count)
      totals.avg_score_sum += normalizeNumber(stats.avg_score) * w
      totals.avg_score_weight += w
    }

    for (const d of (sentimentDist || [])) {
      const c = normalizeNumber(d.count)
      if (d.sentiment === 'positive') dist.positive += c
      else if (d.sentiment === 'negative') dist.negative += c
      else if (d.sentiment === 'neutral') dist.neutral += c
    }

    for (const w of (wordList || [])) {
      const key = w.keyword
      const c = normalizeNumber(w.count)
      wordCloud.set(key, (wordCloud.get(key) || 0) + c)
    }

    for (const t of (topicSentiment || [])) {
      const key = `${t.aspect}|${t.sentiment}`
      const c = normalizeNumber(t.count)
      topicRows.set(key, (topicRows.get(key) || 0) + c)
    }
  }

  const avg_score = totals.avg_score_weight > 0 ? (totals.avg_score_sum / totals.avg_score_weight) : 0
  const sentimentDistribution = [
    { sentiment: 'positive', count: dist.positive },
    { sentiment: 'negative', count: dist.negative },
    { sentiment: 'neutral', count: dist.neutral }
  ]
  const wordCloudList = Array.from(wordCloud.entries()).map(([keyword, count]) => ({ keyword, count }))
    .sort((a, b) => b.count - a.count).slice(0, 50)
  const topicSentimentList = Array.from(topicRows.entries()).map(([key, count]) => {
    const [aspect, sentiment] = key.split('|')
    return { aspect, sentiment, count }
  })

  return {
    stats: { ...totals, avg_score },
    sentimentDistribution,
    wordCloud: wordCloudList,
    topicSentiment: topicSentimentList
  }
}

function computeStrategyFromTopic(ownTopicRows = [], ownGlobalTotal = 0, target = 'sentiment', compTopicRows = [], compGlobalTotal = 0) {
  const byAspect = new Map() // aspect -> { pos, neg, neu, total }
  for (const r of ownTopicRows || []) {
    const a = r.aspect
    if (!byAspect.has(a)) byAspect.set(a, { pos: 0, neg: 0, neu: 0, total: 0 })
    const entry = byAspect.get(a)
    const c = normalizeNumber(r.count)
    if (r.sentiment === 'positive') entry.pos += c
    else if (r.sentiment === 'negative') entry.neg += c
    else if (r.sentiment === 'neutral') entry.neu += c
    entry.total += c
  }

  const compByAspect = new Map()
  for (const r of compTopicRows || []) {
    const a = r.aspect
    if (!compByAspect.has(a)) compByAspect.set(a, { pos: 0, neg: 0, neu: 0, total: 0 })
    const entry = compByAspect.get(a)
    const c = normalizeNumber(r.count)
    if (r.sentiment === 'positive') entry.pos += c
    else if (r.sentiment === 'negative') entry.neg += c
    else if (r.sentiment === 'neutral') entry.neu += c
    entry.total += c
  }

  const unionAspects = new Set([...byAspect.keys(), ...compByAspect.keys()])
  const pool = []

  for (const aspect of unionAspects) {
    const o = byAspect.get(aspect) || { pos: 0, neg: 0, neu: 0, total: 0 }
    const c = compByAspect.get(aspect) || { pos: 0, neg: 0, neu: 0, total: 0 }
    const total = normalizeNumber(o.total)
    const posRate = total > 0 ? normalizeNumber(o.pos) / total : 0
    const negRate = total > 0 ? normalizeNumber(o.neg) / total : 0
    const neuRate = total > 0 ? normalizeNumber(o.neu) / total : 0
    const coverage = (ownGlobalTotal > 0 && total > 0) ? (total / ownGlobalTotal) : 0

    const compPosRate = normalizeNumber(c.total) > 0 ? normalizeNumber(c.pos) / normalizeNumber(c.total) : 0
    const absorbPotential = Math.max(0, compPosRate - posRate)

    let priorityScore = 0
    if (target === 'sentiment') {
      priorityScore = 0.4 * negRate + 0.4 * coverage + 0.2 * (1 - posRate)
    } else if (target === 'competitor') {
      priorityScore = 0.5 * absorbPotential + 0.3 * negRate + 0.2 * coverage
    } else { // ai
      priorityScore = 0.35 * negRate + 0.35 * coverage + 0.3 * Math.max(absorbPotential, 1 - posRate)
    }
    priorityScore = clamp(priorityScore, 0, 1)

    pool.push({
      aspect,
      pos: normalizeNumber(o.pos),
      neg: normalizeNumber(o.neg),
      neu: normalizeNumber(o.neu),
      total: normalizeNumber(o.total),
      posRate, negRate, neuRate, coverage,
      absorbPotential,
      priorityScore
    })
  }

  return pool.sort((a, b) => b.priorityScore - a.priorityScore).slice(0, 20)
}

export async function buildStrategyPool(params) {
  const { mode, selectedObjects = [], target = 'sentiment', compMode = 'series', selectedCompetitors = [], baseFilters = {}, selectedAspects = [] } = params || {}
  // 自身汇总
  const ownSummary = await aggregateSelectionSummary(mode, selectedObjects, baseFilters)
  // 竞品汇总（仅在对比目标时）
  const compSummary = target === 'competitor'
    ? await aggregateSelectionSummary(compMode, selectedCompetitors, baseFilters)
    : { stats: { total_count: 0 }, topicSentiment: [] }

  let pool = computeStrategyFromTopic(
    ownSummary.topicSentiment,
    normalizeNumber(ownSummary.stats.total_count),
    target,
    compSummary.topicSentiment,
    normalizeNumber(compSummary.stats.total_count)
  )

  if (selectedAspects && selectedAspects.length) {
    const sel = new Set(selectedAspects)
    pool = pool.filter(p => sel.has(p.aspect))
  }

  return { strategyPool: pool, ownSummary, compSummary }
}

export function predictOutcomes(strategyPool = []) {
  return (strategyPool || []).map(item => {
    const positive_gain = clamp(0.5 * item.negRate + 0.5 * (item.absorbPotential || 0), 0, 0.35)
    const negative_risk = clamp(0.1 * item.neuRate + 0.2 * item.coverage, 0, 0.25)
    const probability = clamp(0.5 + 0.5 * item.priorityScore, 0.1, 0.95)
    const impactScore = (positive_gain - negative_risk) * probability
    return {
      aspect: item.aspect,
      positive_gain,
      negative_risk,
      probability,
      impactScore
    }
  }).sort((a, b) => b.impactScore - a.impactScore)
}

export async function prepareContext(payload, filters = {}, userRole = 'PM', userRoleValue = '产品经理') {
  const { mode, selectedObjects, target, competitors = {}, strategyPool = [], baseFilters, selectedAspects = [] } = payload || {}
  const { strategyPool: pool, ownSummary, compSummary } = await buildStrategyPool({
    mode,
    selectedObjects,
    target,
    compMode: competitors.mode,
    selectedCompetitors: competitors.selectedObjects || [],
    baseFilters: baseFilters || filters || {},
    selectedAspects
  })

  const predictions = predictOutcomes(pool)

  return {
    timestamp: new Date().toISOString(),
    filters: baseFilters || filters || {},
    target,
    selectedObjects,
    competitors,
    selectedAspects,
    userRole,
    userRoleValue,
    aggregateStats: ownSummary.stats,
    sentimentDistribution: ownSummary.sentimentDistribution,
    wordCloud: ownSummary.wordCloud,
    topicSentiment: ownSummary.topicSentiment,
    compAggregateStats: compSummary.stats,
    compTopicSentiment: compSummary.topicSentiment,
    strategyPool: pool,
    predictions
  }
}

export async function getTopAspects(params) {
  const { mode, selectedObjects = [], baseFilters = {} } = params || {}
  const { stats, topicSentiment } = await aggregateSelectionSummary(mode, selectedObjects, baseFilters)
  const byAspect = new Map()
  for (const r of topicSentiment || []) {
    const a = r.aspect
    if (!byAspect.has(a)) byAspect.set(a, { pos: 0, neg: 0, neu: 0, total: 0 })
    const e = byAspect.get(a)
    const c = normalizeNumber(r.count)
    if (r.sentiment === 'positive') e.pos += c
    else if (r.sentiment === 'negative') e.neg += c
    else if (r.sentiment === 'neutral') e.neu += c
    e.total += c
  }

  const totalAll = normalizeNumber(stats.total_count)
  const list = Array.from(byAspect.entries()).map(([aspect, v]) => {
    const total = normalizeNumber(v.total)
    const posRate = total > 0 ? normalizeNumber(v.pos) / total : 0
    const negRate = total > 0 ? normalizeNumber(v.neg) / total : 0
    const neuRate = total > 0 ? normalizeNumber(v.neu) / total : 0
    const coverage = (totalAll > 0 && total > 0) ? (total / totalAll) : 0
    return { aspect, total, pos: v.pos, neg: v.neg, neu: v.neu, posRate, negRate, neuRate, coverage }
  })

  return list.sort((a, b) => b.total - a.total).slice(0, 20)
}

export default {
  buildStrategyPool,
  predictOutcomes,
  prepareContext,
  getTopAspects
}